/**
 * Overrides de typographie Nuxt UI — mise en conformité avec la maquette.
 *
 * La maquette (Figma « Logiciel », node 1651:50797) n'utilise qu'une seule
 * taille de texte : 16px avec une interligne de 24px, soit `text-base`. Nuxt UI
 * compose ses composants de formulaire en `text-sm` (14px) à la taille `md`,
 * d'où un écart systématique de 2px sur les labels, les saisies, les valeurs de
 * select et les libellés de boutons.
 *
 * Portée volontairement limitée à la taille `md`, celle par défaut : le projet
 * compte 27 usages explicites de `size="xs|sm|lg|xl"` qui sont des choix
 * délibérés et ne doivent pas bouger.
 *
 * Trois mécanismes sont à l'œuvre, chacun imposé par la structure du thème :
 *
 * 1. `variants.size.md` — un override de variante imbriquée *fusionne* avec le
 *    thème d'origine : paddings, gaps et tailles d'icônes sont conservés, seule
 *    la classe de taille de texte est remplacée.
 *
 * 2. `compoundVariants` — la famille `input` et les selects portent un
 *    downgrade responsive `md:text-sm` au-delà de 768px, déclaré en compound
 *    variant. Or les compounds sont appliqués *après* les variantes : un
 *    `md:text-base` posé dans `variants.size.md` perdrait donc le merge. Il
 *    faut le poser à son tour en compound, ciblé sur `size: 'md'`.
 *
 *    La prop `fixed` de Nuxt UI (« Keep the mobile text size on all
 *    breakpoints ») neutralise aussi ce downgrade, mais via
 *    `defaultVariants.fixed` elle s'applique à *toutes* les tailles et fait
 *    grossir de 2px les usages `xs`/`sm`/`lg` sur desktop — écartée pour cette
 *    raison. Elle reste le bon outil au cas par cas, sur une instance donnée.
 *
 * 3. Forme chaîne vs objet — `inputNumber` est le seul dont `variants.size.md`
 *    est une chaîne et non un objet par slot ; son override doit donc être une
 *    chaîne, sinon il ne fusionne pas et la taille d'origine subsiste.
 */

/** Composants dont le thème porte le downgrade responsive `md:text-sm`. */
const neutraliseDowngrade = [{ size: 'md' as const, class: 'md:text-base' }]

const champTexte = {
  variants: { size: { md: { base: 'text-base' } } },
  compoundVariants: neutraliseDowngrade,
}

const champSelect = {
  variants: { size: { md: { base: 'text-base', item: 'text-base' } } },
  compoundVariants: neutraliseDowngrade,
}

export default defineAppConfig({
  ui: {
    // Labels, descriptions, hints et messages d'erreur des champs.
    // `root` porte la taille et se propage aux slots enfants.
    // Pas de downgrade responsive sur ce composant.
    formField: {
      variants: { size: { md: { root: 'text-base' } } },
    },

    // Famille input. Tous ces composants se composent sur le thème de `input`
    // et partagent donc le même défaut — y compris ceux pas encore utilisés,
    // pour éviter une régression silencieuse à leur mise en service.
    input: champTexte,
    inputMenu: champTexte,
    inputTags: champTexte,
    inputDate: champTexte,
    inputTime: champTexte,
    textarea: champTexte,
    pinInput: champTexte,

    // Seul cas où `variants.size.md` est une chaîne et non un objet par slot.
    inputNumber: {
      variants: { size: { md: 'text-base' } },
      compoundVariants: neutraliseDowngrade,
    },

    // Selects. `item` est aligné sur `base` pour ne pas avoir un déclencheur à
    // 16px et des options à 14px dans le même contrôle.
    select: champSelect,
    selectMenu: champSelect,

    // Libellés de boutons. Pas de downgrade responsive.
    button: {
      variants: { size: { md: { base: 'text-base' } } },
    },
  },
})
